import { FC, useCallback, useMemo, useState } from 'react';
import { EmptyStateIcons, EmptyStateProps, Table } from '@silverfort/sf-components';
import { UserRow } from './types';
import { getTableColumns } from './consts';
import { useUsers } from '../../context/UserManagementContext';
import { UsersFilters } from '../../types/filters';
import { isAccountMatchingEnabled } from '../../utils';
import { User } from '../../types/User';
import { UserStatus } from '../../types/userStatus';
import SubUsersTable from './SubUsersTable';

interface Props {
    height?: number;
    selectedRowsIds: UserRow['id'][];
    onSelectRows: (selectedRowsIds: UserRow['id'][]) => void;
}

const TABLE_HEADER_HEIGHT = 40;
const SPACING_BETWEEN_TABLE_HEADER_AND_BODY = 8;
const TABLE_HEIGHT_TO_DECREASE = TABLE_HEADER_HEIGHT + SPACING_BETWEEN_TABLE_HEADER_AND_BODY;

const userKeyExtractor = (user: UserRow) => {
    return user.id;
};

export const getStatusFromUser = (user: Pick<User, 'status' | 'excludeTill'>): UserRow['status'] => {
    const status = user.status;

    if (status === UserStatus.Excluded && user.excludeTill) {
        return {
            type: status,
            excludedTill: user.excludeTill
        };
    }

    return {
        type: status,
        excludedTill: undefined as never
    };
};

const convertUsersToUserRows = (users: User[], hasAccountMatching: boolean): UserRow[] => {
    return users.map((user) => {

        let email;

        if (user.isManualEmail) {
            email = user.email;
        } else {
            email = user.adEmail ?? '';
        }

        return {
            id: user.guid,
            name: { fullName: user.displayName , userName:hasAccountMatching ? user.userName : undefined, secondaryUsersCount: user.secondaryUsersCount },
            status: getStatusFromUser(user),
            email: {
                address: email,
                isExternal: user.isExternalEmail,
                isManualEmail: user.isManualEmail
            },
            domain: user.domainName,
            tokens: user.tokens,
            manage: undefined as never,
            expandedData: () => null
        };
    });
};

const hasFilters = (filters: UsersFilters): boolean => {
    return Object.values(filters).some((filter) => {
        if (Array.isArray(filter)) {
            return filter.length > 0;
        }

        return !!filter;
    });
};

const UsersTable: FC<Props> = ({ height, selectedRowsIds, onSelectRows }) => {
    const { users = [], totalUsersCount = 0, hasMore, fetchMore, loading, error, filters, systemData } = useUsers();
    const hasAccountMatching = isAccountMatchingEnabled(systemData);
    const [expandedUsers, setExpandedUsers] = useState<User[]>([]);

    const hasAppliedFilters = hasFilters(filters);
    const bodyHeight = height && height > TABLE_HEIGHT_TO_DECREASE ? height - TABLE_HEIGHT_TO_DECREASE : undefined;

    const handleScrollEnd = useCallback(() => {
        if (hasMore) {
            fetchMore();
        }
    }, [hasMore, fetchMore]);

    const handleRowExpand = useCallback(
        (userId: UserRow['id']) => {
            const user = users.find((user) => user.guid === userId);
            if (user) {
                setExpandedUsers((prevExpandedUsers) => [...prevExpandedUsers, user]);
            }
        },
        [users]
    );

    const handleRowCollapse = useCallback((userId: UserRow['id']) => {
        setExpandedUsers((prevExpandedUsers) => prevExpandedUsers.filter((user) => user.guid !== userId));
    }, []);

    const emptyStateProps: EmptyStateProps = useMemo(() => {
        if (error) {
            return {
                title: 'Error',
                description: (
                    <span>
                        Something went wrong. Try again. If error persists,
                        <br />
                        contact your Silverfort representative.
                    </span>
                ),
                icon: EmptyStateIcons.Error,
                className: 'py-6',
                contentClassName: 'pb-6'
            };
        }

        if (hasAppliedFilters) {
            return {
                title: 'No results',
                description: 'Try refining your search',
                icon: EmptyStateIcons.NoResults,
                className: 'pt-6 pb-12'
            };
        }

        return {
            title: 'No users',
            description: 'Add user to begin populating',
            icon: EmptyStateIcons.NoItems,
            className: 'py-6'
        };
    }, [hasAppliedFilters, error]);

    const userRows = useMemo(
        () =>
            convertUsersToUserRows(users, hasAccountMatching).map((row) => {
                const expandedUser = expandedUsers.find((user) => user.guid === row.id);
                const userHasSubUsers = (row?.name?.secondaryUsersCount ?? 0) > 0;

                if (expandedUser && userHasSubUsers) {
                    return {
                        ...row,
                        expandedData: <SubUsersTable primaryUser={expandedUser} />
                    };
                }
                if (!userHasSubUsers) {
                    const {expandedData, ...rest0fRow} = row;
                    return {...rest0fRow, expandedData: undefined};
                }

                return row;
            }),
        [users, hasAccountMatching, expandedUsers]
    );

    return (
        <Table
            selectable
            expandableRowsMode={hasAccountMatching}
            expandedDataKey="expandedData"
            tableContainerClassName={`${userRows.length ? 'h-full' : 'h-fit'} w-full overflow-hidden`}
            rowClassName="last:mb-4"
            columns={getTableColumns(users.length, totalUsersCount)}
            bodyHeight={bodyHeight}
            rows={userRows}
            keyExtractor={userKeyExtractor}
            selected={selectedRowsIds}
            onSelect={onSelectRows}
            onScrollEnd={handleScrollEnd}
            loading={loading}
            EmptyStateProps={emptyStateProps}
            onExpand={handleRowExpand}
            onCollapse={handleRowCollapse}
        />
    );
};

export default UsersTable;
